import { Stack } from "@mui/material"
import PageContainer from "../../../components/global/PageContainer"
import FadeFromTop from "../../../components/animations/FadeFromTop"
import { useContext, useMemo, useState } from "react"
import Input from "../../../components/global/Input"
import Text from "../../../components/global/Text"
import Button from "../../../components/global/Button"
import { useTranslation } from "react-i18next"
import axios from "axios"
import { AuthContext } from "../../../controllers/auth"
import { fetchAuthSession } from "aws-amplify/auth"
import Alert from "../../../components/global/Alert"

const Support = () => {
  const { t } = useTranslation()
  const { getUser, team } = useContext(AuthContext)

  // form loading
  const [loading, setLoading] = useState<boolean>(false)

  // form data
  const [name, setName] = useState<string>("")
  const [description, setDescription] = useState<string>("")

  // form submit disabled
  const submitDisabled: boolean = useMemo(
    () => !name || !description,
    [name, description]
  )

  // form alerts
  const [successAlertOpen, setSuccessAlertOpen] = useState<boolean>(false)
  const [errorAlertOpen, setErrorAlertOpen] = useState<boolean>(false)
  const [error, setError] = useState<{ status: number; message: string }>()

  return (
    <PageContainer
      title={t("support")}
      breadcrumbs={[{ title: t("support") }]}
      withFilters={false}
      withDatePicker={false}
      withExportButton={false}
      paddingTop={0}
    >
      <FadeFromTop style={{ width: "100%", marginTop: 40 }}>
        <form
          onSubmit={async (e) => {
            e.preventDefault()
            setLoading(true)

            try {
              const session = await fetchAuthSession()
              const accessToken = session.tokens?.accessToken.toString()

              const user = await getUser()

              await axios.post(
                import.meta.env.VITE_ODOO_ENDPOINT,
                {
                  jsonrpc: "2.0",
                  method: "call",
                  params: {
                    service: "object",
                    method: "execute",
                    args: [
                      "helpdesk.ticket",
                      "create",
                      {
                        team_id: 1, // support area
                        name: name.trim(),
                        description: description.trim(),
                        tag_ids: [[6, 0, [71]]],
                        x_studio_team_id: team.id,
                        x_studio_id_utente: user.sub,
                        partner_email: user.email,
                        x_studio_team_name: team.title,
                        x_studio_piattaforma: "web",
                        x_studio_client: "Dashboard",
                        x_studio_client_id: import.meta.env
                          .VITE_USER_WEB_CLIENT_ID,
                      },
                    ],
                  },
                  id: Date.now(),
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: accessToken,
                  },
                }
              )

              setSuccessAlertOpen(true)
            } catch (e) {
              setError({
                status: e.status,
                message: e.message,
              })
              setErrorAlertOpen(true)
            } finally {
              setLoading(false)
            }
          }}
        >
          <Stack gap={5}>
            <Stack gap={2}>
              <Stack gap={1}>
                <Text id="support-subject" fontWeight={500}>
                  {t("support_subject_label")}
                </Text>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  labelledBy="support-subject"
                  placeholder={t("support_subject_placeholder")}
                  disabled={loading}
                />
              </Stack>
              <Stack gap={1}>
                <Text id="support-description" fontWeight={500}>
                  {t("support_description_label")}
                </Text>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  labelledBy="support-description"
                  placeholder={t("support_description_placeholder")}
                  disabled={loading}
                  multiline
                />
              </Stack>
            </Stack>
            <Button
              primary
              style={{ width: 220, alignSelf: "center" }}
              submit
              disabled={submitDisabled}
              loading={loading}
            >
              {t("support_send_ticket")}
            </Button>
          </Stack>
        </form>
      </FadeFromTop>
      <Alert
        open={successAlertOpen}
        title={t("support_ticket_sent_title")}
        description={t("support_ticket_sent_description")}
        primaryActionLabel={t("ok")}
        primaryActionOnClick={() => {
          setSuccessAlertOpen(false)
          setName("")
          setDescription("")
        }}
      />
      <Alert
        open={errorAlertOpen}
        title={t("error_title")}
        description={error ? `${error.status} | ${error.message}` : t("retry")}
        primaryActionLabel={t("ok")}
        primaryActionOnClick={() => setErrorAlertOpen(false)}
      />
    </PageContainer>
  )
}

export default Support
